import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './AudioPlayer.css';

// R2 CDN base URL
const R2_CDN_URL = 'https://cdn.solarmaktab.com';

// Playback speed options
const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export interface AudioPlayerProps {
  src: string;                // R2 path (relative) or full URL
  title: string;
  titleFa?: string;
  duration?: number;          // Duration in seconds (optional, will be detected)
  onEnded?: () => void;
  onError?: (error: Error) => void;
}

export function AudioPlayer({ src, title, titleFa, duration: initialDuration, onEnded, onError }: AudioPlayerProps) {
  const { i18n } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  
  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buffered, setBuffered] = useState(0);
  
  const isRTL = i18n.language === 'fa';
  const displayTitle = isRTL && titleFa ? titleFa : title;
  
  // Build full audio URL
  const audioUrl = src.startsWith('http') ? src : `${R2_CDN_URL}/${src}`;

  // Format time as MM:SS or HH:MM:SS
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle play/pause toggle
  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error('Error playing audio:', err);
        const errorMsg = isRTL ? 'خطا در پخش صوت' : 'Error playing audio';
        setError(errorMsg);
        onError?.(err);
      });
    }
  }, [isPlaying, isRTL, onError]);

  // Handle seek
  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !audioRef.current || duration <= 0) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  // Handle volume change
  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;
    
    const newMuted = !isMuted;
    audioRef.current.muted = newMuted;
    setIsMuted(newMuted);
  }, [isMuted]);

  // Change playback speed
  const changePlaybackSpeed = useCallback((speed: number) => {
    if (!audioRef.current) return;
    
    audioRef.current.playbackRate = speed;
    setPlaybackSpeed(speed);
    setShowSpeedMenu(false);
  }, []);

  // Skip forward/backward
  const skip = useCallback((seconds: number) => {
    if (!audioRef.current) return;
    
    const newTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  // Retry loading audio
  const handleRetry = useCallback(() => {
    setError(null);
    setIsLoading(true);
    
    if (audioRef.current) {
      audioRef.current.load();
    }
  }, []);

  // Audio event handlers
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoading(false);
    }
  }, []);

  const handleProgress = useCallback(() => {
    if (audioRef.current && audioRef.current.buffered.length > 0) {
      const bufferedEnd = audioRef.current.buffered.end(audioRef.current.buffered.length - 1);
      const bufferedPercent = (bufferedEnd / audioRef.current.duration) * 100;
      setBuffered(bufferedPercent);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    onEnded?.();
  }, [onEnded]);

  const handleAudioError = useCallback(() => {
    const errorMessage = isRTL 
      ? 'خطا در بارگذاری صوت. لطفاً دوباره تلاش کنید.'
      : 'Error loading audio. Please try again.';
    setError(errorMessage);
    setIsLoading(false);
    onError?.(new Error(errorMessage));
  }, [isRTL, onError]);

  const handleWaiting = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handleCanPlay = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Close speed menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showSpeedMenu) {
        setShowSpeedMenu(false);
      }
    };

    if (showSpeedMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSpeedMenu]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if audio player is focused
      const container = audioRef.current?.closest('.audio-player');
      if (!container?.contains(document.activeElement)) {
        return;
      }
      
      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skip(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skip(10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume(v => {
            const newVol = Math.min(1, v + 0.1);
            if (audioRef.current) audioRef.current.volume = newVol;
            return newVol;
          });
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume(v => {
            const newVol = Math.max(0, v - 0.1);
            if (audioRef.current) audioRef.current.volume = newVol;
            return newVol;
          });
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, skip, toggleMute]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`audio-player ${isRTL ? 'rtl' : 'ltr'}`} tabIndex={0}>
      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onPlay={handlePlay}
        onPause={handlePause}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onProgress={handleProgress}
        onEnded={handleEnded}
        onError={handleAudioError}
        onWaiting={handleWaiting}
        onCanPlay={handleCanPlay}
        preload="metadata"
      />

      {/* Error State */}
      {error && (
        <div className="ap-error">
          <div className="ap-error-content">
            <span className="ap-error-icon">⚠️</span>
            <span className="ap-error-message">{error}</span>
            <button className="ap-retry-btn" onClick={handleRetry}>
              🔄 {isRTL ? 'تلاش مجدد' : 'Retry'}
            </button>
          </div>
        </div>
      )}

      {/* Normal Player UI */}
      {!error && (
        <>
          {/* Title and Info */}
          <div className="ap-header">
            <div className="ap-icon">🎧</div>
            <div className="ap-info">
              <h3 className="ap-title">{displayTitle}</h3>
              <span className="ap-duration">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div 
            className="ap-progress-container" 
            ref={progressRef}
            onClick={handleSeek}
          >
            <div className="ap-progress-bar">
              <div className="ap-buffered" style={{ width: `${buffered}%` }} />
              <div className="ap-progress-fill" style={{ width: `${progress}%` }} />
              <div className="ap-progress-handle" style={{ left: `${progress}%` }} />
            </div>
          </div>

          {/* Controls */}
          <div className="ap-controls">
            <div className="ap-controls-left">
              {/* Skip Backward */}
              <button 
                className="ap-control-btn" 
                onClick={() => skip(-10)} 
                aria-label={isRTL ? '۱۰ ثانیه به عقب' : 'Skip back 10 seconds'}
                title={isRTL ? '۱۰ ثانیه به عقب' : '-10s'}
              >
                ⏪
              </button>

              {/* Play/Pause */}
              <button 
                className="ap-control-btn ap-play-btn" 
                onClick={togglePlay} 
                aria-label={isPlaying ? (isRTL ? 'توقف' : 'Pause') : (isRTL ? 'پخش' : 'Play')}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="ap-spinner-small"></span>
                ) : isPlaying ? (
                  '⏸️'
                ) : (
                  '▶️'
                )}
              </button>

              {/* Skip Forward */}
              <button 
                className="ap-control-btn" 
                onClick={() => skip(10)} 
                aria-label={isRTL ? '۱۰ ثانیه به جلو' : 'Skip forward 10 seconds'}
                title={isRTL ? '۱۰ ثانیه به جلو' : '+10s'}
              >
                ⏩
              </button>
            </div>

            <div className="ap-controls-right">
              {/* Playback Speed */}
              <div className="ap-speed-container">
                <button 
                  className="ap-control-btn ap-speed-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSpeedMenu(!showSpeedMenu);
                  }}
                  aria-label={isRTL ? 'سرعت پخش' : 'Playback speed'}
                >
                  {playbackSpeed}x
                </button>
                {showSpeedMenu && (
                  <div className="ap-speed-menu">
                    {PLAYBACK_SPEEDS.map(speed => (
                      <button
                        key={speed}
                        className={`ap-speed-option ${playbackSpeed === speed ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          changePlaybackSpeed(speed);
                        }}
                      >
                        {speed}x
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Volume */}
              <div className="ap-volume-container">
                <button 
                  className="ap-control-btn" 
                  onClick={toggleMute} 
                  aria-label={isMuted ? (isRTL ? 'صدادار' : 'Unmute') : (isRTL ? 'بی‌صدا' : 'Mute')}
                >
                  {isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="ap-volume-slider"
                  aria-label={isRTL ? 'صدا' : 'Volume'}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
